/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

/**
 * Maps raw Airtable industry values to canonical display names used by the
 * Directory filter chips.
 *
 * Unmapped values pass through as-is — a visible signal to update this map
 * when a new industry value appears in Airtable.
 */
const INDUSTRY_CANONICAL_MAP: Record<string, string> = {
	"Media and Entertainment": "M&E",
	"Game Dev": "Games",
	"Game Dev, VP": "Games",
	"Games, VFX": "Games",
	"Film, TV": "Film/TV",
	"AEC, VR": "AEC",
	"AEC/VP": "AEC",
	"Manufacturing (Automotive/Aerospace)": "Automotive/Aerospace",
	"M&E/Programming": "M&E",
};

/**
 * Map a single industry string to its canonical display name. Returns the
 * input unchanged for unmapped values.
 */
export function normalizeIndustry(raw: string): string {
	return INDUSTRY_CANONICAL_MAP[raw] ?? raw;
}

/**
 * Normalize a list of industry values to canonical display names.
 *
 * Accepts either:
 * - A semicolon-separated string (Instructor Airtable field shape) — splits,
 *   trims, drops empties, maps each, then dedupes.
 * - A string array (Training Partner / Service Partner field shape) — maps
 *   each, then dedupes.
 *
 * Always returns canonical-cased values in first-seen order with duplicates
 * removed.
 */
export function normalizeIndustries(raw: string | string[] | null | undefined): string[] {
	if (!raw) return [];
	const items =
		typeof raw === "string"
			? raw
					.split(";")
					.map((s) => s.trim())
					.filter(Boolean)
			: raw.filter(Boolean);
	return Array.from(new Set(items.map(normalizeIndustry)));
}
